import {
  Box,
  CssBaseline,
  Grow,
  Backdrop as MuiBackdrop,
  Popper,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { StyledEngineProvider, ThemeProvider, createTheme } from '@mui/material/styles'
import { LicenseInfo } from '@mui/x-license-pro'
import { EmptyState } from 'common/components/EmptyState'
import config from 'config'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  ChevronsUpDown,
  Inbox,
  Info,
  X,
} from 'lucide-react'
import { forwardRef, useEffect, useMemo } from 'react'
import { useFavicon, useLocalStorage } from 'react-use'

import { colors } from './colors'
import { fontFamily, fontFamilyMonospace, fontWeight, typography } from './typography'

/**
 * Domain-specific styles for performance reasons
 */
import 'allotment/dist/style.css'
import './Calendar.css'
import './fonts.css'

LicenseInfo.setLicenseKey(config.mui.licenseKey)

const DEFAULT_BORDER_RADIUS = 6

const formElementsPadding = 8

const formElementsSmallHeight = 32
const formElementsHeight = 40
const formElementsLargeHeight = 48

const Backdrop = forwardRef((props, ref) => {
  return (
    <MuiBackdrop
      sx={{
        backdropFilter: 'blur(3px)',
        opacity: 0.9,
      }}
      {...props}
    />
  )
})

/**
 * Material UI Custom Theme
 */

export const Theme = ({ children }) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')

  const systemMode = prefersDarkMode ? 'dark' : 'light'
  const [savedMode, setSavedMode] = useLocalStorage('serverless-theme-mode', 'dark')

  const mode = savedMode === 'system' ? systemMode : savedMode

  const mainAppClass = `app-${mode}-mode`

  const isDarkMode = mode === 'dark'

  /**
   * Update favicon based on current system mode because the favicon is not visible on in active tabs when the icon is dark and the system is in dark mode
   */
  useFavicon(prefersDarkMode ? '/favicon-dark.ico' : '/favicon-light.ico')

  useEffect(() => {
    document.body.classList.add(mainAppClass)
  }, [mode])

  /**
   * Colors
   */

  const primary = {
    main: isDarkMode ? colors.white : colors.blackDark,
    light: isDarkMode ? colors.greyDark : colors.blackLight,
    dark: isDarkMode ? colors.white : colors.blackDark,
    contrastText: isDarkMode ? colors.blackDark : colors.white,
  }

  const secondary = {
    main: isDarkMode ? colors.blackDark : colors.white,
    light: isDarkMode ? colors.blackMedium : colors.greyMedium,
    dark: isDarkMode ? colors.blackDark : colors.white,
    contrastText: isDarkMode ? colors.white : colors.blackDark,
  }

  const error = {
    main: colors.redPrimary,
    light: colors.redLight,
    dark: colors.redDark,
    contrastText: colors.white,
  }

  const info = { ...primary }

  const border = {
    main: isDarkMode ? colors.blackMediumLight : colors.greyMedium,
    light: isDarkMode ? colors.blackUltraLight : colors.greyUltraDark,
    dark: isDarkMode ? colors.blackMediumLight : colors.greyLight,
  }

  const shadow = {
    main: isDarkMode ? 'rgba(255,255,255,0.125)' : 'rgba(0,0,0,0.15)',
  }

  const grey = {
    main: isDarkMode ? colors.blackMediumLight : colors.greyMedium,
    dark: isDarkMode ? colors.blackMedium : colors.greyLight,
    medium: isDarkMode ? colors.blackLight : colors.greyMedium,
    light: isDarkMode ? colors.blackUltraLight : colors.greyDark,
  }

  const text = {
    primary: primary.main,
    secondary: isDarkMode ? colors.greyUltraDark : colors.blackUltraLight,
    red: error.main,
  }

  const background = {
    default: secondary.main,
    paper: secondary.main,
  }

  const cssVars = {
    [`.${mainAppClass}`]: {
      '--primary': primary.main,
      '--secondary': secondary.main,
      '--red': colors.redPrimary,
      '--white': colors.white,
      '--black': colors.blackDark,
      '--greyMain': grey.main,
      '--greyDark': grey.dark,
      '--greyMedium': grey.medium,
      '--greyLight': grey.light,
      '--borderMain': border.main,
      '--borderDark': border.dark,

      // These borders are for the split view panels used by allotment
      '--focus-border': border.main,
      '--separator-border': border.main,

      '--text-primary-color': text.primary,
      '--text-secondary-color': text.secondary,
      '--text-primary-size': typography.textPrimary.fontSize + 'px',
      '--text-secondary-size': typography.textSecondary.fontSize + 'px',
      '--text-tertiary-size': typography.textTertiary.fontSize + 'px',
    },
  }
  const borderRadius = DEFAULT_BORDER_RADIUS
  const sidebarWidth = 55
  const sidebarIconSize = Math.ceil(sidebarWidth * 0.32)

  const shape = { borderRadius, sidebarWidth, sidebarIconSize }
  const palette = {
    systemMode,
    isDarkMode,
    savedMode,
    mode,
    setSavedMode,
    colors,
    primary,
    secondary,
    error,
    info,
    border,
    shadow,
    grey,
    text,
    background,
    action: {
      disabledBackground: grey.medium,
    },
  }
  const MuiButton = useMemo(
    () => ({
      defaultProps: {
        variant: 'contained',
        color: 'primary',
        size: 'medium',
        fullWidth: false,
      },
      variants: [
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            backgroundColor: grey.dark,
            border: `1px solid ${border.main}`,
            '&:hover': {
              backgroundColor: grey.main,
              border: `1px solid ${border.main}`,
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            border: `1px solid ${primary.main}`,
            '&:hover': {
              backgroundColor: grey.dark,
              border: `1px solid ${primary.main}`,
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'error' },
          style: {
            border: `1px solid ${error.main}`,
            '&:hover': {
              backgroundColor: grey.dark,
              border: `1px solid ${error.main}`,
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'secondary' },
          style: {
            color: text.primary,
            border: `1px solid ${border.main}`,
            '&:hover': {
              backgroundColor: grey.dark,
              border: `1px solid ${border.main}`,
            },
          },
        },
        {
          props: { variant: 'text' },
          style: {
            color: text.primary,
            border: `1px solid transparent`,
            '&:hover': {
              backgroundColor: grey.dark,
              border: `1px solid ${border.main}`,
            },
            '&.Mui-disabled': {
              background: 'none ',
              border: `1px solid transparent`,
            },
          },
        },

        {
          props: { variant: 'text', color: 'secondary' },
          style: {
            color: text.secondary,
            border: `1px solid transparent`,
            '&:hover': {
              border: `1px solid ${border.main}`,
              backgroundColor: grey.dark,
              color: text.primary,
            },
          },
        },
        {
          props: { variant: 'text', color: 'error' },
          style: {
            color: error.main,
            '&:hover': {
              backgroundColor: grey.dark,
            },
          },
        },
      ],
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          transition: 'all 0.3s ease',
          width: 'fit-content',
          minWidth: 'fit-content',
          textTransform: 'initial',
          borderRadius: shape.borderRadius,
          fontWeight,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: theme.palette[ownerState.color].light,
            border: ownerState.color === 'secondary' ? `1px solid ${border.light}` : 'none',
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
            transform: 'scale(0.97)',
          },
          '&.Mui-disabled': {
            color: grey.medium,
            backgroundColor: grey.dark,
            borderColor: grey.dark,
            cursor: 'not-allowed',
            '&.MuiButtonGroup-grouped': {
              color: theme.palette.text.secondary,
              background: theme.palette.grey.medium,
              '& .MuiButton-endIcon': {
                pointerEvents: 'auto',
              },
            },
          },
        }),
        startIcon: ({ ownerState }) => {
          return {
            marginRight: 0,
            marginLeft: 0,
            color: 'inherit',
          }
        },
        endIcon: ({ ownerState }) => {
          return {
            marginRight: 0,
            marginLeft: 0,
            color: 'inherit',
          }
        },
        sizeLarge: ({ ownerState }) => {
          /**
           * If the button is an icon button, we want to reduce the padding and set width
           */
          if (ownerState.isIconButton) {
            return {
              padding: '0',
              height: formElementsLargeHeight,
              width: formElementsLargeHeight,
            }
          }
          return {
            padding: `0 ${formElementsPadding + 7}px`,
            height: formElementsLargeHeight,
            fontSize: typography.h6.fontSize,
          }
        },
        sizeMedium: ({ ownerState }) => {
          /**
           * If the button is an icon button, we want to reduce the padding and set width
           */
          if (ownerState.isIconButton) {
            return {
              padding: '0',
              height: formElementsHeight,
              width: formElementsHeight,
            }
          }
          return {
            padding: `0 ${formElementsPadding + 4}px`,
            height: formElementsHeight,
            fontSize: typography.textPrimary.fontSize,
          }
        },
        sizeSmall: ({ ownerState }) => {
          /**
           * If the button is an icon button, we want to reduce the padding and set width
           */
          if (ownerState.isIconButton) {
            return {
              padding: '0',
              height: formElementsSmallHeight,
              width: formElementsSmallHeight,
            }
          }
          return {
            padding: `0 ${formElementsPadding}px`,
            height: formElementsSmallHeight,
            fontSize: typography.textPrimary.fontSize,
          }
        },
        /**
         * Introduce a new button size 'tiny' this is mostly used for buttons with 'isIconButton=true'
         */
        sizeTiny: ({ ownerState }) => {
          /**
           * If the button is an icon button, we want to reduce the padding and set width
           */
          if (ownerState.isIconButton) {
            return {
              padding: `0 ${formElementsPadding - 6}px`,
              height: formElementsSmallHeight - 5,
              width: formElementsSmallHeight - 5,
              display: 'none !important',
            }
          }
          return {
            padding: `0 ${formElementsPadding - 6}px`,
            height: formElementsSmallHeight - 5,
            width: formElementsSmallHeight - 5,
            fontSize: typography.textSecondary.fontSize,
          }
        },

        iconSizeLarge: {},
        iconSizeMedium: {},
        iconSizeSmall: {},
      },
    }),
    [isDarkMode]
  )
  const theme = createTheme({
    shape,
    palette,
    typography,
    components: {
      MuiStepper: {
        defaultProps: {
          connector: (
            <Box
              sx={{
                height: '15px',
                width: '1px',
                backgroundColor: 'grey.medium',
                marginLeft: '10px',
                borderRadius: '2px',
              }}
            />
          ),
        },
        styleOverrides: {
          root: {},
        },
      },
      MuiStepLabel: {
        defaultProps: {},
        styleOverrides: {
          root: {
            padding: '6px 0',
          },
        },
      },
      MuiAccordionSummary: {
        defaultProps: {
          expandIcon: <ChevronRight size={14} />,
        },
        styleOverrides: {
          root: {
            minHeight: '40px',
            padding: '0px 10px',
            margin: '0',
            borderRadius: 4,
            '&:hover': {
              background: grey.dark,
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(90deg)',
            },
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: background.default,
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            '& .MuiDataGrid-withBorderColor': {
              borderColor: border.main,
            },

            '& a': {
              textDecoration: 'none',
            },

            '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within, &.MuiDataGrid-root .MuiDataGrid-cell:focus-within':
              {
                outline: 'none !important',
              },
            '& .MuiToolbar-root .MuiTablePagination-displayedRows': {
              color: text.secondary,
            },
            '& .MuiToolbar-root .MuiTablePagination-actions': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-iconButtonContainer': {
              marginLeft: '8px',
              '& button': {
                fontSize: '1.1rem',
              },
            },

            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: grey.dark,
            },

            '& .MuiDataGrid-cell': {
              borderColor: 'border.main',
              color: text.secondary,
            },

            '& .MuiDataGrid-cellContent': {
              color: text.secondary,
            },

            '& .MuiDataGrid-columnHeaderTitleContainer': {
              color: text.secondary,
            },
            '& .MuiDataGrid-pinnedColumnHeaders': {
              background: 'secondary.main',
              backgroundImage: 'none',
            },
            '& .MuiDataGrid-pinnedColumns': {
              background: 'secondary.main',
              backgroundImage: 'none',
            },
          },
        },
      },
      MuiBadge: {
        defaultProps: { color: 'primary' },
        styleOverrides: {
          root: {
            '& .MuiBadge-badge': {
              border: `2px solid ${secondary.main}`,
            },
          },
        },
      },
      MuiAlertTitle: {
        defaultProps: { gutterBottom: false },
        styleOverrides: {
          root: {
            fontSize: typography.textPrimary.fontSize,
          },
        },
      },
      MuiAlert: {
        defaultProps: {
          variant: 'outlined',
          severity: 'info',
          color: 'primary',
          iconMapping: {
            info: <Info size={18} />,
            error: <AlertTriangle size={18} />,
            warning: <AlertCircle size={18} />,
            success: <CheckCircle size={18} />,
          },
        },
        variants: [
          {
            props: { variant: 'text' },
            style: {
              color: text.primary,
              border: 'none',
              backgroundColor: 'none',
              padding: 0,
            },
          },
          {
            props: { variant: 'text', color: 'secondary' },
            style: {
              color: text.secondary,
              border: 'none',
              backgroundColor: 'none',
            },
          },
          {
            props: { variant: 'contained' },
            style: {
              color: secondary.main,
              borderColor: primary.main,
              backgroundColor: primary.main,
            },
          },
          {
            props: { variant: 'contained', severity: 'error' },
            style: {
              color: error.contrastText,
              borderColor: error.main,
              backgroundColor: error.main,
            },
          },
          {
            props: { variant: 'contained', color: 'secondary' },
            style: {
              color: text.secondary,
              borderColor: grey.dark,
              backgroundColor: grey.dark,
            },
          },

          {
            props: { variant: 'outlined' },
            style: {
              color: primary.main,
              borderColor: border.main,
              '& .MuiAlert-icon': {
                color: primary.main,
              },
            },
          },
          {
            props: { variant: 'outlined', severity: 'error' },
            style: {
              color: error.main,
              borderColor: error.main,

              '& .MuiAlert-icon': {
                color: error.main,
              },
            },
          },
          {
            props: { variant: 'outlined', color: 'secondary' },
            style: {
              color: text.secondary,
              borderColor: border.main,
              '& .MuiAlertTitle-root': {
                color: text.primary,
              },
              '& .MuiAlert-icon': {
                color: text.primary,
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadius,
          },
          icon: {
            padding: '9px 0',
          },
          action: {
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            // margin: 'auto 0 auto auto',
          },
          message: {
            padding: '9px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            justifyContent: 'center',
          },
        },
      },

      MuiCircularProgress: {
        defaultProps: {
          color: 'primary',
        },
      },
      MuiLinearProgress: {
        defaultProps: {
          color: 'primary',
        },
        styleOverrides: {
          root: {
            height: 6,
            borderRadius: 4,
            backgroundColor: grey.medium,
            '& .MuiLinearProgress-dashed': {
              backgroundImage: 'none',
              animation: 'none',
              background: grey.medium,
            },
          },
        },
      },
      /**
       * Used for checkbox with label
       */
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              opacity: 0.6,
            },
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {},
        styleOverrides: {
          root: {
            borderRadius: 4,
            transition: 'all 0.3s ease',
            padding: 0,
            '&:hover': {
              backgroundColor: 'none',
            },
            '&.Mui-checked': {
              // backgroundColor: primary.main,
            },
          },
        },
      },
      /**
       * Divider border color is either light or secondary. It's light by default.
       */
      MuiDivider: {
        defaultProps: {
          light: true,
        },
        variants: [
          {
            props: { dashed: true },
            style: {
              borderStyle: 'dashed',
            },
          },
        ],
        styleOverrides: {
          root: {
            borderColor: primary.main,
          },
          light: {
            borderColor: border.main,
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          size: 'small',
          IconComponent: (props) => <ChevronsUpDown size={14} {...props} />,
          autoWidth: true,
        },
        variants: [
          {
            props: { size: 'small' },
            style: {
              '& .MuiInputBase-input.MuiSelect-select': {
                height: formElementsSmallHeight,
                padding: `0 ${formElementsPadding + 6}px`,
              },
            },
          },
          {
            props: { size: 'medium' },
            style: {
              '& .MuiInputBase-input.MuiSelect-select': {
                height: formElementsHeight,
                padding: `0 ${formElementsPadding + 8}px`,
              },
            },
          },
          {
            props: { size: 'large' },
            style: {
              '& .MuiInputBase-input.MuiSelect-select': {
                height: formElementsLargeHeight,
                padding: `0 ${formElementsPadding + 10}px`,
              },
            },
          },
        ],
        styleOverrides: {
          root: {},
          select: {
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          },
          '& input': {
            backgroundColor: 'red',
            display: 'none',
            width: 'fit-content',
            height: 'fit-content',
          },
          icon: {
            right: `${formElementsPadding}px`,
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            color: primary.main,
            height: '2px',
          },
          rail: {
            height: '2px',
          },

          valueLabel: {
            backgroundColor: primary.main,
            color: secondary.main,
          },
          mark: {
            backgroundColor: secondary.main,
            height: '4px',
            borderRadius: 0,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius,
          },
        },
      },
      MuiChip: {
        variants: [
          ...MuiButton.variants,
          {
            props: { size: 'small' },
            style: {
              padding: `0px ${formElementsPadding}px`,
              fontSize: typography.textTertiary.fontSize,
              height: formElementsSmallHeight - 12,
            },
          },
          {
            props: { size: 'medium' },
            style: {
              padding: `0px ${formElementsPadding + 2}px`,
              fontSize: typography.textSecondary.fontSize,
              height: formElementsSmallHeight - 8,
            },
          },
          {
            props: { size: 'large' },
            style: {
              padding: `0px ${formElementsPadding + 4}px`,
              fontSize: typography.textPrimary.fontSize,
              height: formElementsSmallHeight,
            },
          },
        ],
        defaultProps: {
          deleteIcon: <X size={15} />,
        },
        styleOverrides: {
          root: {
            width: 'fit-content',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 3,
            gap: `${formElementsPadding - 2}px`,
            padding: `0px ${formElementsPadding - 2}px`,
            fontSize: typography.textSecondary.fontSize,
            height: formElementsSmallHeight - 8,
          },
          label: {
            padding: 0,
            lineHeight: 1.05,
            marginTop: '1px',
            display: 'flex',
          },
          deleteIcon: {
            margin: 'auto 0',
          },
          avatar: {
            color: 'currentColor',
            marginRight: 0,
            marginLeft: 0,
            width: 'auto',
            height: 'auto',
            marginBottom: 2,
            // display: 'none',
          },
        },
      },

      MuiSwitch: {
        defaultProps: {
          color: 'secondary',
        },
        styleOverrides: {
          root: {
            width: 40,
            height: 16,
            padding: 0,
            display: 'flex',
            '&:active': {
              '& .MuiSwitch-thumb': {
                width: 15,
              },
              '& .MuiSwitch-switchBase.Mui-checked': {
                transform: 'translateX(17px)',
              },
            },
            '& .MuiSwitch-switchBase': {
              padding: 2,
              transform: 'translateX(1px)',
              color: colors.white,
              '&.Mui-disabled': {
                color: colors.white,
                '& + .MuiSwitch-track': {
                  backgroundColor: grey.light,
                },
              },
              '&.Mui-checked': {
                transform: 'translateX(24px)',
                color: secondary.main,
                '& + .MuiSwitch-track': {
                  opacity: 1,
                  backgroundColor: primary.main,
                },
              },
            },
            '& .MuiSwitch-thumb': {
              boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
              width: 11,
              height: 11,
              marginTop: '0.25px',
              borderRadius: 6,
              transition: 'width 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            },
            '& .MuiSwitch-track': {
              borderRadius: 16 / 2,
              opacity: 1,
              backgroundColor: isDarkMode ? colors.greyUltraDark : colors.greyDark,
              boxSizing: 'border-box',
            },
          },
        },
      },

      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
          focusRipple: false,
        },
        styleOverrides: {
          root: {
            transition: 'all 0.3s ease',
            gap: 10,
            width: 'fit-content',
          },
        },
      },
      MuiButton,
      MuiToggleButton: {
        defaultProps: {
          size: 'small',
          exclusive: true,
        },
        styleOverrides: {
          root: {
            padding: 0,
            textTransform: 'none',
          },
          sizeLarge: MuiButton.styleOverrides.sizeLarge,
          sizeMedium: MuiButton.styleOverrides.sizeMedium,
          sizeSmall: MuiButton.styleOverrides.sizeSmall,
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '5px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 4,
            color: isDarkMode ? colors.white : colors.blackDark,
            transition: 'all .2s ease-in-out',
            '&.Mui-disabled': {
              color: colors.greyUltraDark,
            },
          },
        },
      },

      MuiTabs: {
        defaultProps: {
          indicatorColor: 'primary',
          variant: 'scrollable',
          scrollButtons: false,
        },
        styleOverrides: {
          root: {
            position: 'relative',
            zIndex: 6,
            display: 'flex',
            alignItems: 'flex-end',
            bottom: 0,
            minHeight: shape.sidebarWidth,
            marginTop: 0,
          },
          indicator: {
            height: 1,
          },
          flexContainer: {},
          scroller: {
            minHeight: shape.sidebarWidth,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          },
        },
      },

      MuiTab: {
        defaultProps: {
          disableFocusRipple: true,
        },
        /**
         * Add a new variant for throttled tabs to reduce their opacity
         */
        variants: [{ props: { isThrottled: true }, style: { opacity: 0.5 } }],
        styleOverrides: {
          root: {
            textTransform: 'none',
            padding: 0,
            minWidth: 60,
            height: shape.sidebarWidth,
            margin: '0 20px 0 0',
            fontSize: typography.textPrimary.fontSize,
            transition: 'all .2s ease-in-out',
            minHeight: 'auto',
            marginRight: '20px',
            gap: '5px',
            '& svg': {
              marginRight: '8px',
            },
            '&.Mui-selected': {
              color: primary.main,
            },
            '&:hover': {
              color: primary.main,
            },
          },
        },
      },

      MuiMenu: {
        defaultProps: {
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          transitionDuration: { appear: 400, enter: 400, exit: 200 },
          TransitionComponent: Grow,
        },

        styleOverrides: {
          paper: ({ theme, ownerState, ...props }) => {
            return {
              backgroundImage: 'none',
              borderRadius: borderRadius + 4,
              boxShadow: 'none',
              border: `1px solid ${border.main}`,
              maxHeight: 400,
              marginTop: '5px',
              minWidth: ownerState?.anchorEl?.offsetWidth,
              '& .MuiMenuItem-root': {
                width: '100%',
                padding: '8px 15px',
                fontSize: typography.textSecondary.fontSize,
                transition: 'all 0.3s ease',
                backgroundColor: background.default,
                '&.Mui-selected': {
                  backgroundColor: grey.dark,
                  '&:hover': {
                    backgroundColor: grey.dark,
                  },
                },
                '&:hover': {
                  backgroundColor: grey.dark,
                },
              },
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }
          },
        },
      },

      MuiTooltip: {
        defaultProps: {
          placement: 'top',
          arrow: true,
          enterDelay: 500,
          TransitionProps: {
            timeout: 0,
          },
        },
        styleOverrides: {
          tooltip: {
            backgroundColor: isDarkMode ? colors.white : colors.blackDark,
            color: isDarkMode ? colors.blackDark : colors.white,
            fontSize: 11,
            padding: '8px 16px',
            borderRadius: 7,
          },
          arrow: {
            color: isDarkMode ? colors.white : colors.blackDark,
          },
        },
      },

      MuiPaper: {
        defaultProps: {
          square: true,
          elevation: 0,
        },
      },

      MuiNativeSelect: {
        defaultProps: {
          disableUnderline: true,
        },
      },

      MuiAutocomplete: {
        defaultProps: {
          noOptionsText: 'No options',
          disableClearable: true,
          limitTags: 2,
          size: 'small',
          freeSolo: true,
          filterSelectedOptions: true,
          ChipProps: {
            deleteIcon: <X size={15} />,
            size: 'small',
            style: { cursor: 'pointer' },
          },
          renderInput: (params) => <TextField {...params} />,

          PopperComponent: (props) => {
            return (
              <Popper
                {...props}
                style={{ ...props.style }}
                children={
                  !props.children.props.children.some((child) => child) ? (
                    <Box
                      sx={{
                        border: `1px solid ${border.main}`,
                        p: 1,
                        backgroundColor: 'secondary.main',
                        width: '100%',
                        minWidth: '100%',
                        borderRadius: '4px',
                      }}
                    >
                      <EmptyState
                        label="No options found"
                        icon={<Inbox size={25} />}
                        textVariant="textSecondary"
                        sx={{ gap: '10px', padding: '20px' }}
                      />
                    </Box>
                  ) : (
                    props.children
                  )
                }
              />
            )
          },
        },
        variants: [
          {
            props: { size: 'large' },
            style: {
              input: {
                height: formElementsLargeHeight,
                fontSize: typography.textSecondary.fontSize,
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            padding: 0,
            width: '100%',
            border: 'none',

            '& .MuiAutocomplete-tag': {
              '&:not(.MuiButtonBase-root)': {
                fontSize: typography.textSecondary.fontSize,
              },
            },

            '& .MuiInputBase-root': {
              padding: '0 !important',
              '& .MuiAutocomplete-input': {},
              '&.Mui-focused': {
                position: 'relative',
                '& .MuiAutocomplete-input': {},
              },
            },
          },
          input: {
            padding: '0px 12px !important',
          },
          paper: {
            borderRadius: 4,
            maxWidth: '100%',
            marginTop: 1,
            border: `1px solid ${border.main}`,
            backgroundImage: 'none',
          },
          fieldSet: {
            backgroundColor: 'red',
          },
          listbox: {
            padding: 0,
            boxSizing: 'border-box',
            '& li': {
              color: text.secondary,
              fontSize: typography.textSecondary.fontSize,
              fontWeight: 100,
              padding: '10px 20px !important',

              '&[aria-disabled="true"]': {
                opacity: '0.6 !important',
              },
              '&:hover': {
                color: text.primary,
              },
              '& .Mui-checked': {
                '& svg': {
                  color: primary.main,
                },
              },
            },
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          },
          noOptions: {
            color: '#777',
            padding: '6px 16px',
            fontSize: typography.textPrimary.fontSize,
            fontStyle: 'italic',
          },
        },
      },

      MuiPopover: {
        defaultProps: {
          transitionDuration: 0,
        },
        styleOverrides: {},
      },

      MuiDialog: {
        defaultProps: {
          transitionDuration: { appear: 400, enter: 400, exit: 200 },
          TransitionComponent: Grow,
          slots: {
            backdrop: Backdrop,
          },
        },
        styleOverrides: {
          root: {},
          paper: {
            borderRadius: 10,
            background: secondary.main,
            boxShadow: `none`,
            border: `1px solid ${border.main}`,
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: typography.h3.fontSize,
            borderBottom: `1px solid ${border.main}`,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '24px',
            paddingTop: '24px !important',
            overflowY: 'auto',
          },
        },
        variants: [
          {
            props: { flex: true },
            style: {
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            },
          },
        ],
      },
      MuiDialogContentText: {
        defaultProps: {
          component: Typography,
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '15px',
            borderTop: `1px solid ${border.main}`,
          },
        },
      },

      MuiGrid: {
        styleOverrides: {
          container: {
            '&$spacing-xs-1': {
              width: `calc(100% + ${2.5 * 1}px)`,
              margin: -2.5,
              '& $item': {
                padding: 2.5,
              },
            },
          },
        },
      },

      MuiList: {
        styleOverrides: {
          root: {
            paddingTop: '0px !important',
            paddingBottom: '0px !important',

            '& .MuiDivider-root': {
              marginTop: 0,
              marginBottom: 0,
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            transition: 'all 0.3s ease',
            padding: 5,
            borderRadius: 4,
            /**
             * On hover but not selected
             */
            '&:not(.Mui-selected):hover': {
              // paddingLeft: 18,
              backgroundColor: grey.dark,
            },
            '&.Mui-selected': {
              backgroundColor: grey.dark,
              '&:hover': {
                backgroundColor: grey.dark,
              },
            },
          },
        },
      },

      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'medium',
          fullWidth: true,
        },
        styleOverrides: {
          root: {
            '& input:-webkit-autofill,input:-webkit-autofill:hover,input:-webkit-autofill:focus': {
              WebkitBoxShadow: `0 0 0px 100000px ${grey.dark} inset`,
              WebkitTextFillColor: primary.main,
            },
            '& .Mui-disabled ': {
              opacity: 0.7,
              cursor: 'not-allowed',
            },
            '& .MuiInputBase-multiline ': {
              padding: `${formElementsPadding}px 0`,
            },
          },
        },
      },
      MuiInputBase: {
        defaultProps: {},
        variants: [
          /**
           * TextField size=large is not supported by Mui so we create a custom variant for it
           */
          {
            props: { size: 'large' },
            style: {
              height: formElementsLargeHeight,
              input: {
                height: formElementsLargeHeight,
                fontSize: typography.textSecondary.fontSize,
              },
            },
          },
          {
            props: { size: 'large' },
            style: {
              height: formElementsLargeHeight,
              input: {
                height: formElementsLargeHeight,
                fontSize: typography.textSecondary.fontSize,
              },
            },
          },
          {
            props: { size: 'large' },
            style: {
              height: formElementsLargeHeight,
              input: {
                height: formElementsLargeHeight,
                fontSize: typography.textSecondary.fontSize,
              },
            },
          },
        ],

        styleOverrides: {
          root: {
            borderRadius: 4,
            transition: 'all ease 0.25s',
            padding: 0,
            boxSizing: 'border-box',
            height: formElementsHeight,
            '&.MuiInput-root:before': {
              display: 'none',
            },
            '&.MuiInput-root.Mui-focused:after': {
              display: 'none',
            },
          },
          input: {
            padding: '0px 12px',
            fontSize: typography.textSecondary.fontSize,
            height: formElementsHeight,

            '&::placeholder': {
              opacity: 1,
              color: grey.medium,
              fontWeight,
            },
          },

          sizeSmall: {
            height: formElementsSmallHeight,
            input: {
              height: formElementsSmallHeight,
              fontSize: typography.textSecondary.fontSize,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            border: `1px solid ${border.main} !important`,

            '&.MuiInputBase-adornedEnd': {
              paddingRight: '4px',
            },
            '&.Mui-focused': {
              border: `1px solid ${primary.main} !important`,
            },
          },
          input: {
            padding: `0 ${formElementsPadding}px`,
            fontSize: typography.textSecondary.fontSize,
            '&.MuiInputBase-inputAdornedStart': {},
          },
          notchedOutline: {
            border: 'none',
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            border: `1px solid ${border.main} !important`,
            backgroundColor: grey.dark,
            '&.MuiInputBase-adornedEnd': {
              paddingRight: '4px',
            },
            '&.Mui-focused': {
              border: `1px solid ${primary.main} !important`,
            },
          },
          input: {
            padding: `0 ${formElementsPadding}px`,
            fontSize: typography.textSecondary.fontSize,
            '&.MuiInputBase-inputAdornedStart': {},
          },
          notchedOutline: {
            border: 'none',
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
          disableAnimation: true,
          color: 'primary',
        },
        variants: [
          {
            props: { color: 'secondary' },
            style: { color: text.secondary },
          },
        ],
        styleOverrides: {
          root: {
            position: 'relative',
            transform: 'none',
            fontSize: typography.textSecondary.fontSize,
            marginBottom: 5,
            color: primary.main,
            '&.Mui-focused': {
              color: primary.main,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginTop: '8px',
            color: text.secondary,
            fontSize: typography.textSecondary.fontSize,
            transition: 'all 0.3s ease',
            /**
             * Add helper text animation for when there's an error and the text suddenly appears
             */
            animation: 'show 300ms 50ms cubic-bezier(0.38, 0.97, 0.56, 0.76) forwards',
            // Prestate
            // opacity: 0,
            // remove transform for just a fade-in
            // transformOrigin: 'top center',
            // maxHeight: 0,
            // '@keyframes show': {
            //   '100%': {
            //     maxHeight: 'auto',
            //     opacity: 0.7,
            //   },
            // },
            '& .Mui-disabled ': {
              color: grey.medium,
            },
            '&.Mui-error': {},
          },

          contained: {
            marginLeft: 0,
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            width: 'auto',
            height: 'auto',

            '& svg': {
              stroke: grey.medium,
            },
          },
          positionStart: {
            marginRight: 0,
          },
        },
      },

      MuiAvatar: {
        styleOverrides: {
          root: {
            position: 'initial',
          },
        },
      },

      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: primary.main,
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            borderTop: `1px solid ${border.main}`,
            justifyContent: 'flex-end',
            padding: '15px',
          },
        },
      },

      MuiCssBaseline: {
        styleOverrides: {
          html: {
            fontSize: 14,
            fontWeight,
            textRendering: 'optimizeLegibility',
            mozOsxFontSmoothing: 'grayscale',
            webkitFontSmoothing: 'antialiased',
            fontFamily,
            /**
             * These variables are used in different files such as the
             * index.css, Calendar.css to style colors for both themes
             */
            ...cssVars,
          },

          /**
           * The follow are global styles which are defined here for performance reasons.
           * These styles will get injected once onto the page, and used on the app-list page.
           */

          '.app-list-settings-link': {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '5px',
            fontWeight: 'bold',
            outline: 'none',
            textDecoration: 'none',
            userSelect: 'none',
            cursor: 'pointer',
          },
          '.app-list-instance-row': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            marginLeft: 0,
            cursor: 'pointer',
            transition: 'all 0.2s ease',

            '& .MuiTypography-root': {
              transition: 'all 0.25s ease',
            },

            '&:hover': {
              backgroundColor: 'var(--greyDark)',
              cursor: 'pointer',

              '& .MuiTypography-root': {
                opacity: 1,
                cursor: 'pointer',
                color: 'var(--text-primary-color)',
              },
            },

            '& div': {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1,
            },
          },

          /**
           * Just a global style lighten some of the styles with react-json-view
           */

          '.studio .object-content .object-key-val *': {
            backgroundColor: 'transparent !important',
            opacity: `1 !important`,
          },

          '.studio .object-key, .studio .object-key *': {
            color: '#999 !important',
            opacity: `1 !important`,
            fontFamily: fontFamilyMonospace,
          },

          '.studio .object-key-val, .studio .string-value, .studio .node-ellipsis': {
            color: `${text.primary} !important`,
            fontFamily: fontFamilyMonospace,
          },

          '.studio .variable-value *': {
            color: `${text.primary} !important`,
            fontFamily: fontFamilyMonospace,
          },

          '.studio .object-content svg': {
            color: 'white !important',
          },

          '.studio .object-content .object-key-val.object-key-val': {
            borderLeft: '1px solid rgb(64 64 64) !important',
            paddingTop: '0px !important',
            paddingBottom: '0px !important',
          },

          '.studio .object-content .variable-row': {
            borderLeft: '1px solid rgb(64 64 64) !important',
            paddingTop: '0px !important',
            paddingBottom: '0px !important',
          },

          '.studio .pretty-json-container > .object-content > .object-key-val': {
            borderLeft: 'none !important',
          },

          /**
           * Monaco overrides
           */
          '.studio .mtk4': {
            color: '#949494 !important',
          },

          '.studio .mtk5': {
            color: '#ffffff !important',
          },
          'body, #root': {
            height: '100%',
            fontSize: 14,
            fontWeight,
            fontFamily,
          },

          'html, body, div, h1, h2, h3, h4, h5, h6, p, span, input, a, li': {
            fontFamily,
          },

          '*::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },

          '*::-webkit-scrollbar': {
            width: 6,
            height: 6,
          },

          '*::-webkit-scrollbar-corner': {
            backgroundColor: 'transparent',
          },

          '*::-webkit-scrollbar-thumb': {
            background: grey.medium,
            borderRadius: 4,
          },

          '.ellipsis': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },

          '.hidden-scrollbars': {
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              width: 0,
              height: 0,
            },
          },
          /**
           * Add allotment split view styles
           */
          /**
           * Add grip icon to allotment vertical an horizontal sashes
           */
          '.sash-vertical, .sash-horizontal': {
            '&::after': {
              '--width': 1,
              position: 'relative',
              content: '"⠿"',
              color: text.primary,
              backgroundColor: grey.main,
              fontSize: 14,
              padding: '2px 0',
              lineHeight: '1',
              width: '12px',
              borderRadius: '3px',
              top: '50%',
              left: '50%',
              display: 'flex', // setting display to inline-block to be able to use transform
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'translate(-50%,-50%) ',
              // padding: '0 calc(var(--width) * 1px)',
            },
          },
          '.sash-horizontal': {
            '&::after': {
              top: '50%',
              left: '50%',

              transform: 'translate(-50%,-50%) rotateZ(90deg)',
            },
          },
          '.sash': { zIndex: 3 },
          '.split-view-view::before': { zIndex: '1 !important' },
          /**
           * Logs stream styles
           */
          '.log-stream-box': {
            padding: '0',
            width: '100%',
            transition: 'all 0.28s ease',
          },
          '.log-stream-box:hover': {
            cursor: 'pointer',
            backgroundColor: 'var(--greyDark)',
            width: '100%',
          },
          '.log-stream-box.compactMode:hover,\n.log-stream-box.internal-message:hover': {
            cursor: 'default !important',
            backgroundColor: 'transparent !important',
          },
          '.log-stream-box p': { fontFamily: "'Roboto Mono', monospace" },
          '.log-stream-box pre': { fontFamily: "'Roboto Mono', monospace" },
          '.log-stream-box a': { fontFamily: "'Roboto Mono', monospace" },
          '.log-stream-box.internal-message': { cursor: 'default' },
          '.log-stream-header': {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
          },
          '.log-stream-header p': {
            margin: '0 30px 10px 0',
            color: '#999999',
            fontSize: '13px',
          },
          '.log-stream-header.internal-message p': {
            marginRight: '30px',
            color: '#999999',
            fontSize: '13px',
          },
          '.log-stream-header.internal-message.error-message p': {
            marginRight: '30px',
            color: '#fd5750',
            fontSize: '13px',
          },
          '.log-stream-header.internal-message pre': {
            marginRight: '30px',
            color: '#999999',
            fontSize: '13px',
          },
          '.log-stream-header.internal-message pre a': {
            color: '#999999',
            fontSize: '13px',
            textDecoration: 'none',
          },
          '.log-stream-header.internal-message.error-message pre': {
            marginRight: '30px',
            color: '#fd5750',
            fontSize: '13px',
          },
          '.log-stream-body': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexGrow: 1,
          },
          '.log-stream-log': { flexGrow: 1, margin: '0 0 0 10px', fontSize: '13px' },
          '.log-stream-log *': {
            fontFamily: "'Roboto Mono', monospace",
            letterSpacing: '0.15px',
            fontSize: '13px',
            whiteSpace: 'pre-line',
          },
          '.log-stream-log.error': {
            fontFamily: "'Roboto Mono', monospace",
            color: '#fd5750',
          },
          '.log-stream-log.clickable .react-json-view': { cursor: 'pointer !important' },
          '.react-json-view .copy-to-clipboard-container': {
            display: 'none !important',
          },
          '.react-json-view:hover\n  .pretty-json-container\n  > .object-content\n  > div\n  > span\n  > div\n  > .copy-to-clipboard-container':
            {
              display: 'inherit !important',
            },
          '.log-stream-header p,\n.log-stream-header pre,\n.log-stream-log,\n.react-json-view': {
            whiteSpace: 'pre-wrap',
            overflow: 'hidden',
            wordBreak: 'break-all',
          },
          /**
           * Charts styles
           */
          '.recharts-bar-click .recharts-bar-rectangle': { cursor: 'pointer' },
          '.scatter-clickable .recharts-layer.recharts-scatter-symbol': {
            cursor: 'pointer',
          },
          /**
           * Notistack snackbar styles
           *
           */
          '.notistack-SnackbarContainer .notistack-Snackbar .notistack-MuiContent': {
            backgroundColor: 'var(--secondary)',
            color: 'var(--primary)',
            border: '1px solid var(--borderMain)',
            boxShadow: 'none',
          },
          '.notistack-SnackbarContainer .notistack-Snackbar .notistack-MuiContent div': {
            gap: '10px',
          },
          '.notistack-SnackbarContainer .notistack-Snackbar .notistack-MuiContent button .leftright,\n.notistack-SnackbarContainer .notistack-Snackbar .notistack-MuiContent button .rightleft':
            {
              backgroundColor: 'var(--greyMedium)',
            },
          '.notistack-SnackbarContainer .notistack-Snackbar .notistack-MuiContent.notistack-MuiContent-error':
            {
              backgroundColor: 'var(--red)',
              border: '1px solid var(--red)',
              color: 'var(--white)',
            },
          '.notistack-SnackbarContainer\n  .notistack-Snackbar\n  .notistack-MuiContent.notistack-MuiContent-error\n  button\n  .leftright,\n.notistack-SnackbarContainer\n  .notistack-Snackbar\n  .notistack-MuiContent.notistack-MuiContent-error\n  button\n  .rightleft':
            {
              backgroundColor: 'var(--white)',
            },
          '@keyframes indeterminateAnimation': {
            '0%': { opacity: 0, transform: 'translateX(-35%) scaleX(0.35)' },
            '50%': { opacity: 1 },
            '100%': { opacity: 0, transform: 'translateX(100%) scaleX(0.35)' },
          },
        },
      },
    },
  })

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <StyledEngineProvider injectFirst>{children}</StyledEngineProvider>
    </ThemeProvider>
  )
}
